// src/pages/usa/puertorico/Aguadilla.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";
import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Aguadilla ALWAYS goes back to Puerto Rico map
export const backRoute = "/map/puertorico";

export default function Vieques() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Vieques, Puerto Rico</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
            <h2>🏞️ Discover Vieques, Puerto Rico</h2>

<p>
  Vieques is a small island located just east of the main island of Puerto Rico, known for its
  quiet atmosphere, natural beauty, and relaxed pace of life. For decades, large portions of
  Vieques were used by the United States Navy as a military training and bombing range, a period
  that shaped much of the island’s modern history until operations ended in the early 2000s.
  Today, Vieques is peaceful, uncrowded, and ideal for travelers looking for a calm place to stay.
  One of its most important historic landmarks is <strong>Fortín Conde de Mirasol</strong>, also
  known as Fuerte de Vieques — the last fort built by the Spanish in the Americas. Construction
  began in <strong>1845</strong> and the fort now houses the Vieques Museum of Art and History,
  preserving the island’s cultural heritage. 
</p>


            <h3>🚗 Getting There</h3>

<p>
  To reach Vieques, you’ll need to take the ferry from the <strong>Ceiba Ferry Terminal </strong> 
  on Puerto Rico’s east coast. (Ferries used to depart from Fajardo years ago, but all service
  has now moved to Ceiba.) The ferry ride to Vieques takes about <strong>30–60 minutes</strong>,
  depending on the vessel. 
</p>

<p>
  Ferry trips start early in the morning, and lines can form long before departure. When I visited
  years ago, people would begin lining up around <strong>3:00 AM</strong> to secure a spot. While
  operations have improved since then, it’s still smart to arrive early—especially on weekends,
  holidays, or high‑season months.
</p>

<p>
  To guarantee your spot, it’s highly recommended to make a reservation in advance. You can buy
  tickets online through the official ferry website:
  <br />
  <a
  href="https://www.puertoricoferry.com"
  target="_blank"
  rel="noopener noreferrer"
>
  https://www.puertoricoferry.com
</a>

  <br />
  This site provides schedules, ticket availability, and real‑time updates. 
</p>

<p>
  Make sure you reserve <strong>both your departure and return tickets</strong> to avoid any issues
  getting back to Ceiba. Same‑day tickets are sometimes available at the terminal, but they can sell
  out during busy periods.
</p>

<p>
  You can also take your <strong>car on the cargo ferry</strong> for an additional fee, but this
  requires a separate reservation and availability is limited. The official ferry website provides
  all the details on vehicle transport, pricing, and requirements. 
</p>



            <h3>🌟 My Personal Observation</h3>

<ul>
  <li>
    <div className="place-block">
  <strong>Esperanza Neighborhood:</strong> A charming area with a laid‑back vibe.
  <br />
  The town of Esperanza is the main hub of Vieques, offering a mix of local culture,
  restaurants, and small shops. It’s a great place to stroll around, enjoy fresh seafood,
  and experience the island’s relaxed atmosphere. I stayed in a small guesthouse here and
  loved the quiet streets, colorful buildings, and friendly locals. Most of the stores and
  restaurants are family‑owned businesses, and there’s no loud nightlife or big hotels —
  which is part of the charm. During my last visit, many restaurants and shops were closing
  around <strong>8:00 PM</strong>, so it’s best to plan your evenings accordingly.
  <br /><br />
  Esperanza is very walkable, and you can easily explore the area on foot. There are also
  several small beaches nearby that are worth checking out. The neighborhood is located on
  the <strong>southern coast of Vieques</strong>, while the ferries arrive on the northern
  coast, so renting a car or taking a taxi is the easiest way to get there from the ferry
  terminal.
  <br /><br />
  In my experience, I’ve tried both options. I once brought my own car on the ferry, which
  made it easy to explore the island and reach more remote areas. On another visit, I used
  public transportation for the ride from the ferry terminal to Esperanza.  Planning ahead 
  is key, as taxis can be scarce and public transportation schedules may not always align
   with ferry arrivals. If you choose to rent a car, make sure to book in advance, especially 
   during peak travel seasons, to ensure availability.
</div>

  </li>

  <li>
    <div className="place-block">
      <strong>Bioluminicence Bay:</strong> The Bio Bay is one of the most famous attractions in Vieques, known for its
       glowing waters at night.  I have been in 3 different bioluminescent bays in Puerto Rico, and the one in Vieques 
       is by far the brightest and most magical. The bay is home to millions of tiny organisms called dinoflagellates 
       that emit a bright blue light when disturbed.  Kayaking through the bay at night is an unforgettable experience, as 
       every paddle stroke creates a trail of glowing light in the water. It’s best to visit on a moonless night for the 
       brightest display, and there are several tour operators on the island that offer guided kayak tours to the Bio Bay. 
       Just remember to book your tour in advance, as spots can fill up quickly, especially during peak tourist seasons.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Driving Around Vieques:</strong> The island is small and easy to explore by car. Vieques has a network of paved 
      and unpaved roads that allow you to access various beaches, viewpoints, and hidden gems. Renting a car or bringing your own 
      is highly recommended to fully experience the island’s beauty and tranquility. Some of the most popular spots include 
      Playa Caracas (Red Beach), Playa Negra (Black Beach), and the Vieques National Wildlife Refuge. The drive itself is scenic, 
      with greenery, coastal views, and occasional sightings of wild horses roaming freely. Just be sure to drive carefully on 
      unpaved roads, especially after rain, as they can become slippery.  Also, keep in mind that some areas of the island may 
      have limited cell service, so it’s a good idea to have a physical map or download offline maps before you go.  Take under cosideration
      that Vieques was a military training ground for decades, and there are still some restricted areas where access is prohibited
       due to unexploded ordnance. Always follow local guidelines and signage to ensure a safe visit.
    </div>
  </li>

</ul>


            <h3>🥾 Other Hidden Gems</h3>

<ul>
  <li>
    <div className="place-block">
      <strong>Abandoned Military Bunkers:</strong>
      <br />
      Vieques was used for decades as a U.S. Navy training and bombing range, and many of the old
      concrete bunkers still remain scattered across the island. When I visited years ago, some of
      the roads around the bunkers were open to drive through, but caution is essential. These
      structures are abandoned, aging, and can weaken over time. Avoid climbing on them and always
      check for safety before approaching. They offer a fascinating look into the island’s past and
      make for unique photo opportunities.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Fiestas Patronales de Vieques:</strong>
      <br />
      Vieques celebrates its annual <em>Fiestas Patronales</em> during the summer months, usually in
      <strong>July</strong>. These festivals take place in Isabel II or Esperanza and include live
      music, food kiosks, artisans, parades, and community events. It’s one of the island’s biggest
      celebrations and a great way to experience local culture. Dates can vary each year, so it’s
      best to confirm locally or check community calendars when planning your trip.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Noche de San Juan:</strong>
      <br />
      On the night of <strong>June 23</strong>, Vieques joins the rest of Puerto Rico in celebrating
      Noche de San Juan. Locals and visitors gather at the beaches just before midnight and walk
      backward into the water for good luck. It’s a simple but meaningful tradition that feels even
      more magical on Vieques’ quiet shores.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Local Community Events:</strong>
      <br />
      Throughout the year, Vieques hosts small artisan markets, environmental programs, and cultural
      gatherings—often in Esperanza or Isabel II. These events vary by season and offer a chance to
      connect with residents while enjoying local food, crafts, and music.
    </div>
  </li>
</ul>





            <h3>🛏️ Places to Stay in Vieques</h3>

<p>
  Vieques offers a mix of boutique hotels, small guesthouses, and locally owned stays.
  Many properties are listed on <strong>Booking.com</strong> and <strong>Expedia</strong>,
  while others operate independently. Always double‑check availability and reviews before booking.
</p>

<ul>
  <li>
    <div className="place-block">
      <strong>El Blok Hotel:</strong>
      A modern, adults‑only boutique hotel located on the Esperanza Malecón.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Blue Horizon Boutique Resort:</strong>
      An adults‑only oceanfront property with a pool and beautiful views.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Club Vieques – Adults Only Resort:</strong>
      A highly rated, peaceful resort with a pool and excellent service.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Sea Gate Hotel:</strong>
      A friendly, budget‑friendly hotel near Isabel II with great reviews.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Villa Coral:</strong>
      A comfortable guesthouse in Esperanza within walking distance of restaurants and the beach.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Finca Victoria:</strong>
      A beautiful wellness‑focused boutique hotel in the hills of Vieques.
      <br />
      <em>Available on Expedia (check Booking.com for seasonal availability)</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Hacienda Tamarindo:</strong>
      A charming hilltop hotel with ocean views and a relaxing atmosphere.
      <br />
      <em>Available on Booking.com & Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Waterside Inn:</strong>
      A simple, budget‑friendly stay right in Esperanza.
      <br />
      <em>Available on Expedia</em>
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Local Guesthouses & Independents:</strong>
      Vieques also has many small, family‑owned stays not listed on major platforms.
      These include private casitas, eco‑lodges, and small inns—great for travelers
      who prefer a more local experience.
      <br />
      <em>Local‑only (not on Booking.com or Expedia)</em>
    </div>
  </li>
</ul>



            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>

            <h3>🎥 My Travel Videos</h3>

            <p>
              I do not have a full video for Vieques.  Will Be Posted Soon
            </p>

            <p>
              <a
                href="https://youtu.be/0dOeAGZSVgM?si=cS-qT7pYBHhepqEy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Puerto Rico
              </a>
            </p>


          </div>

          {/* RIGHT COLUMN */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/0dOeAGZSVgM?si=cS-qT7pYBHhepqEy"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/puertorico/vieques/beach.jpg"
                alt="Beach"
              />
            </a>

           
            <img
              src="/images/usa/puertorico/vieques/beach3.jpg"
              alt="Beach"
            />
            <img
              src="/images/usa/puertorico/vieques/beach4.jpg"
              alt="Beach"
            />
            <img
              src="/images/usa/puertorico/vieques/beach5.jpg"
              alt="Beach"
            />
            <img
              src="/images/usa/puertorico/vieques/bunker.jpg"
              alt="Bumker"
            />
             <img
              src="/images/usa/puertorico/vieques/bunker2.jpg"
              alt="Bunker"
            />
             <img
              src="/images/usa/puertorico/vieques/bunker3.jpg"
              alt="Bunker"
            />
             <img
              src="/images/usa/puertorico/vieques/hotel.jpg"
              alt="Hotel"
            />
             <img
              src="/images/usa/puertorico/vieques/malecon.jpg"
              alt="Malecon"
            />
             <img
              src="/images/usa/puertorico/vieques/port.jpg"
              alt="Port"
            />
             <img
              src="/images/usa/puertorico/vieques/street.jpg"
              alt="Street"
            />
            <img
              src="/images/usa/puertorico/vieques/sunset2.jpg"
              alt="Sunset"
            />
          
          </div>
        </section>
      </div>

      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
